import { useRef, useState, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import TWEEN from '@tweenjs/tween.js';
import classNames from 'classnames';
import ViewManager from './ViewManager_new';
window.loadedObjects = {};

function extractTexturesFromMaterial(material, textures) {
	// Вземаме всички възможни типове текстури от материала
	//English: We take all possible types of textures from the material
	const textureProperties = ['map', 'bumpMap', 'normalMap', 'roughnessMap', 'metalnessMap', 'alphaMap', 'emissiveMap', 'aoMap', 'displacementMap'];

	textureProperties.forEach((prop) => {
		if (material[prop]) {
			textures.push(material[prop]);
		}
	});
}



function getTexturesAndMaterials(object) {
	let materials = [];
	let textures = [];

	object.traverse((child) => {
		// Проверяваме дали обектът има материал
		// English: Check if the object has a material
		if (child.isMesh && child.material) {
			// Ако има повече от един материал (масив от материали)
			// English: If there is more than one material (array of materials)
			if (Array.isArray(child.material)) {
				child.material.forEach((material) => {
					materials.push(material);
					// Извличаме текстурите от този материал
					// English: Extract textures from this material
					extractTexturesFromMaterial(material, textures);
				});
			} else {
				// Добавяме материала към списъка
				// English: Add the material to the list
				materials.push(child.material);
				// Извличаме текстурите от материала
				// English: Extract textures from the material
				extractTexturesFromMaterial(child.material, textures);
			}
		}
	});

	return { materials, textures };
}

const isObjectEmpty = (objectName) => {
	for (let prop in objectName) {
		if (objectName.hasOwnProperty(prop)) {
			return false;
		}
	}
	return true;
};

let mainCameraSetPostion = false;
const setCameraToLookAtPoint = (camera, controls, object) => {
	if (mainCameraSetPostion) { return };

	if (!object) {
		console.warn('No object provided to center on');
		return;
	}

	const boundingBox = new THREE.Box3().setFromObject(object);
	const center = boundingBox.getCenter(new THREE.Vector3());
	const size = boundingBox.getSize(new THREE.Vector3());

	const maxDim = Math.max(size.x, size.y, size.z);
	const zoom = 0.8;
	const fov = camera.fov * (Math.PI / 180);
	const cameraDistance = Math.abs(maxDim / Math.sin(fov / 2)) * zoom;

	const direction = new THREE.Vector3().subVectors(camera.position, center).normalize();
	// camera.position.copy(direction.multiplyScalar(cameraDistance).add(center)); // No Move Camera position

	camera.lookAt(center);

	if (controls) {
		controls.target.copy(center);
		controls.update();
	}

	camera.updateProjectionMatrix();
	// console.log('setCameraToLookAtPoint');
	mainCameraSetPostion = true;
};

function Configurator({ selectedData, model, modelTop, modelTopColor, modelValance, modelFringe,  modelBottom }) {
	const [showCanvas, setShowCanvas] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const [views, setViews] = useState([]);
	const controlsRef = useRef(null);
	const sceneRef = useRef(null);
	const cameraRef = useRef(null);
	const rendererRef = useRef(null);
	const loaderRef = useRef(null);
	const configuratorMediaRef = useRef(null);
	const [loader] = useState(new GLTFLoader());

	const centerCamera = useCallback(() => {
		const mainObject = sceneRef.current.getObjectByName('main');

		if (mainObject && cameraRef.current && controlsRef.current) {
			setCameraToLookAtPoint(cameraRef.current, controlsRef.current, mainObject);
		}
	}, []);

	const getViews = useCallback(() => {
		if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
		if (!sceneRef.current) return;

		const newViews = [
			{ position: new THREE.Vector3(36, 18.3, -9.3), lookAtObject: 'main' },
			{ position: new THREE.Vector3(21, 42, -13), lookAtObject: 'top' },
			{ position: new THREE.Vector3(15, 2.3, -19), lookAtObject: 'top' },
			{ position: new THREE.Vector3(2.9, 6, -6.2), lookAtObject: 'bottom' }
		];

		const viewCamera = new THREE.PerspectiveCamera(100, rendererRef.current.domElement.clientWidth / rendererRef.current.domElement.clientHeight, 0.1, 1000);

		const thumbnails = newViews.map(view => {
			let toObject = sceneRef.current.getObjectByName(view.lookAtObject) || sceneRef.current.getObjectByName('main');
			const boundingBox = new THREE.Box3().setFromObject(toObject);
			const objectCenter = boundingBox.getCenter(new THREE.Vector3());
			viewCamera.position.copy(view.position);
			viewCamera.lookAt(objectCenter);
			rendererRef.current.render(sceneRef.current, viewCamera);

			return {
				thumbnail: rendererRef.current.domElement.toDataURL(),
				position: view.position,
				target: objectCenter
			};
		});

		setViews(thumbnails);
	}, []);

	function updateTextureImage(texture, newImageSrc) {
		// Create a new image object
		const loader = new THREE.TextureLoader();
	
		// Load the new image
		loader.load(
			newImageSrc,
			function (newTexture) {
				// Update the image of the existing texture
				texture.image = newTexture.image;
				texture.needsUpdate = true;
	
				// console.log('Texture updated successfully');
				getViews();
			},
			undefined,
			function (error) {
				console.error('An error occurred while loading the new texture:', error);
			}
		);
	}

	const handleResize = useCallback(() => {
		if (configuratorMediaRef.current && rendererRef.current && cameraRef.current) {
			const width = configuratorMediaRef.current.clientWidth;
			const height = configuratorMediaRef.current.clientHeight;
			rendererRef.current.setSize(width, height);
			cameraRef.current.aspect = width / height;
			cameraRef.current.updateProjectionMatrix();
		}
	}, []);

	useEffect(() => {
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [handleResize]);

	useEffect(() => {
		if (configuratorMediaRef.current) {
			sceneRef.current = new THREE.Scene();
			cameraRef.current = new THREE.PerspectiveCamera(50, configuratorMediaRef.current.clientWidth / configuratorMediaRef.current.clientHeight, 0.1, 1000);
			cameraRef.current.position.set(13, 19, -80.44);

			// show grid in x, y, z axis
			// const axesHelper = new THREE.AxesHelper(50);
			// sceneRef.current.add(axesHelper);

			rendererRef.current = new THREE.WebGLRenderer({ antialias: true });
			rendererRef.current.setSize(configuratorMediaRef.current.clientWidth, configuratorMediaRef.current.clientHeight);
			rendererRef.current.toneMapping = THREE.ACESFilmicToneMapping;
			rendererRef.current.toneMappingExposure = 1.5;
			rendererRef.current.shadowMap.enabled = true;


			configuratorMediaRef.current.appendChild(rendererRef.current.domElement);

			const hemisphereLight = new THREE.HemisphereLight(0xfefefe, 0xbbbbff, 0.2);
			sceneRef.current.add(hemisphereLight);

			const ambientLight = new THREE.AmbientLight(0xfefefe, 0.8);
			sceneRef.current.add(ambientLight);

			const directionalLight = new THREE.DirectionalLight(0xf5e7c1, 0.7);
			directionalLight.position.set(0, 50, 50);
			directionalLight.castShadow = false;
			sceneRef.current.add(directionalLight);

			const directionalLight1 = new THREE.DirectionalLight(0xfefefe, 0.8);
			directionalLight1.position.set(5, 10, 7.5);
			directionalLight1.castShadow = false;
			sceneRef.current.add(directionalLight1);

			const directionalLight2 = new THREE.DirectionalLight(0xfefefe, 0.7);
			directionalLight2.position.set(-5, 12, -7.5);
			directionalLight2.castShadow = false;
			sceneRef.current.add(directionalLight2);

			sceneRef.current.background = new THREE.Color(0xfefefe);

			controlsRef.current = new OrbitControls(cameraRef.current, rendererRef.current.domElement);
			controlsRef.current.enableDamping = true;
			controlsRef.current.dampingFactor = 0.25;
			controlsRef.current.rotateSpeed = 0.35;
			controlsRef.current.enableZoom = true;
			controlsRef.current.zoomSpeed = 1.2;

			const animate = () => {
				requestAnimationFrame(animate);
				rendererRef.current.render(sceneRef.current, cameraRef.current);
				TWEEN.update();
				// console.log(cameraRef.current.position);
			};

			animate();
			handleResize();

			return () => {
				if (configuratorMediaRef.current && rendererRef.current) {
					configuratorMediaRef.current.removeChild(rendererRef.current.domElement);
				}
			};
		}
	}, [handleResize]);

	let timeoutId;

	function waitToLoadFiles(callback, delay) {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(callback, delay);
	}

	function delayWaitToLoadFiles(newDelay) {
		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				centerCamera();
				getViews();
				setShowCanvas(true)
				setShowLoader(false);
				// console.log('loaded');
			}, newDelay);
		}
	}

	const updateValanceTexture = () => {
		if (!!!window.loadedObjects["valance"]) return;
		const texturesValance = getTexturesAndMaterials(window.loadedObjects["valance"]).textures
		const valanceTextureUrl = modelTopColor.valancesTextures[modelValance.id];
		if (!!valanceTextureUrl) {
			updateTextureImage(texturesValance[0], valanceTextureUrl);
		}else {
			console.warnn('No texture found for valance');
		}
	}

		const loadModel = (model, modelGroupName) => {
		return new Promise((resolve, reject) => {
			if (!selectedData || isObjectEmpty(model)) {
				removeIfExist(modelGroupName);
				resolve();
				return;
			}

			if (!model.drawing) {
				console.warn(`Failed to load path of ${modelGroupName}`);
				reject(new Error(`Failed to load path of ${modelGroupName}`));
				return;
			}

			setShowLoader(true);

			loader.load(
				model.drawing,
				(gltf) => {
					const object = gltf.scene;
					object.name = modelGroupName;
					removeIfExist(modelGroupName);
					sceneRef.current.add(object);
					// console.log("loaded" + modelGroupName);

					window.loadedObjects[modelGroupName] = object;

					if (modelGroupName === "top") {
						const texturesTop = getTexturesAndMaterials(window.loadedObjects["top"]).textures
						updateTextureImage(texturesTop[0], modelTopColor.topTexture);
					}

					if (modelGroupName === "valance") {
						updateValanceTexture();
					}

					resolve();
				},
				(xhr) => {
					// console.log('loading');
					delayWaitToLoadFiles(1500);
				},
				(error) => {
					console.error(error);
					reject(error);
				}
			);
		});
	};


	waitToLoadFiles(() => { }, 600);//Timeout start

	useEffect(() => {
		if (!!!window.loadedObjects["main"]) {
			loadModel(model, "main");
		}else {
			const texturesStructure = getTexturesAndMaterials(window.loadedObjects["main"]).textures
			const texturePath = model.colors[model.activeColorIndex].texture;
			const texturePath2 = model.colors[model.activeColorIndex].texture2;
			
			updateTextureImage(texturesStructure[0], texturePath);
			updateTextureImage(texturesStructure[2], texturePath2);
		}
	}, [model]);

	useEffect(() => {
		loadModel(modelTop, "top");
	}, [modelTop]);

	useEffect(() => {
		loadModel(modelValance, "valance");
	}, [modelValance]);

	useEffect(() => {
		loadModel(modelFringe, "fringe");
	}, [modelFringe]);

	useEffect(() => {
		if (!!!window.loadedObjects["top"]) return;
		const texturesTop = getTexturesAndMaterials(window.loadedObjects["top"]).textures
		updateTextureImage(texturesTop[0], modelTopColor.topTexture);
		updateValanceTexture();
	}, [modelTopColor]);

	useEffect(() => {
		loadModel(modelBottom, "bottom");
	}, [modelBottom]);

	const removeIfExist = (name) => {
		const existingObject = sceneRef.current.getObjectByName(name);
		if (existingObject) {
			sceneRef.current.remove(existingObject);
		}
	};

	return (
		<div
			ref={configuratorMediaRef}
			style={{ width: '100%', height: '100%' }}
			className={classNames('canvas-holder', { 'is-visible': showCanvas })}
		>

			<div
				ref={loaderRef}
				className={classNames('loader', { 'is-visible': showLoader })}
			>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" className="w-12 h-12">
					<radialGradient id="a11" cx=".66" fx=".66" cy=".3125" fy=".3125" gradientTransform="scale(1.5)">
						<stop offset="0" stopColor="#745C36"></stop>
						<stop offset=".3" stopColor="#745C36" stopOpacity=".9"></stop>
						<stop offset=".6" stopColor="#745C36" stopOpacity=".6"></stop>
						<stop offset=".8" stopColor="#745C36" stopOpacity=".3"></stop>
						<stop offset="1" stopColor="#745C36" stopOpacity="0"></stop>
					</radialGradient>
					<circle
						transform-origin="center"
						fill="none"
						stroke="url(#a11)"
						strokeWidth="15"
						strokeLinecap="round"
						strokeDasharray="200 1000"
						strokeDashoffset="0"
						cx="100"
						cy="100"
						r="70"
					>
						<animateTransform
							type="rotate"
							attributeName="transform"
							calcMode="spline"
							dur="2"
							values="360;0"
							keyTimes="0;1"
							keySplines="0 0 1 1"
							repeatCount="indefinite"
						></animateTransform>
					</circle>
					<circle
						transform-origin="center"
						fill="none"
						opacity=".2"
						stroke="#000"
						strokeWidth="15"
						strokeLinecap="round"
						cx="100"
						cy="100"
						r="70"
					></circle>
				</svg>
			</div>
			<ViewManager
				views={views}
				controls={controlsRef.current}
				camera={cameraRef.current}
			/>
		</div>
	);
}

export default Configurator;